import { useState, useEffect, useContext } from "react";
import classes from "./Cart.module.css";
import Button from "react-bootstrap/Button";
import { useNavigate } from "react-router-dom";
import {
	doc,
	getDoc,
	addDoc,
	serverTimestamp,
	collection,
} from "firebase/firestore";
import { db } from "../index";
import { CartContext } from "./CartContext";
import CartProductList from "./CartProductList";
import AppLoader from "../UI/AppLoader";
import Modal from "react-bootstrap/Modal";
import Form from "react-bootstrap/Form";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import { PaystackButton } from "react-paystack";
import Alert from "react-bootstrap/Alert";
import { blurContext } from "./BlurContext";
import BigNumber from "bignumber.js";

function Cart() {
	// use it to navigate the app routes
	const history = useNavigate();
	const product_context = useContext(CartContext);
	// stringify your product_context so that deep changes can be detected by react, to enable the component re-render
	const product_context_copy = JSON.stringify(product_context.cartItems);

	const [product, setProduct] = useState([]);
	const [loading, setLoading] = useState(true);

	//import context function to blur modal backdrop i.e the app
	const { changeBlurState } = useContext(blurContext);

	// Modal state for payment
	const [showModal, setShowModal] = useState(false);

	//data required for check-out
	const [email, setEmail] = useState("");
	const [name, setName] = useState("");
	const [phone, setPhone] = useState("");
	const [address, setAddress] = useState({
		houseNumber: "",
		street: "",
		town: "",
		state: "",
		country: "",
		postalCode: "",
	});

	const [error, setError] = useState(false);

	//modal state for payment success
	const [paymentSuccess, setPaymentSuccess] = useState(false);

	//modal state for payment abortion
	const [paymentAborted, setPaymentAborted] = useState(false);

	// effect to listen for press of backward or forward buttons in the browser to change blur state to false
	useEffect(() => {
		return () =>
			window.addEventListener("popstate", () => changeBlurState(false));
	}, [changeBlurState]);

	useEffect(() => {
		const loadProducts = async () => {
			//array will be further processed to store item prices
			try {
				if (product_context.cartItems.length === 0) {
					setProduct([]);
					return;
				}

				setLoading(true);
				const data = [];

				// create promises
				const promises = product_context.cartItems.reduce(
					(output, cart_item) => {
						if (typeof output[cart_item.id] === "undefined") {
							//Only add another item to the list if it hasn't already been added
							output[cart_item.id] = getDoc(
								doc(db, "items", cart_item.id)
							);
						}
						return output;
					},
					{}
				);

				const loadItems = await Promise.all(Object.values(promises));
				loadItems.forEach((item) => {
					const cart_items = product_context.cartItems.filter(
						(eachItem) => eachItem.id === item.id
					);
					for (const cart_item of cart_items) {
						const product = item.data();
						const size = product.sizes.find(
							(size) => size.sku === cart_item.size
						);
						data.push({
							id: item.id,
							size: size?.name,
							sku: cart_item.size,
							...item.data(),
							quantity: cart_item.quantity,
						});
					}
				});
				setProduct(data);
			} catch (error) {
				setError(true);
				throw new Error(error);
			} finally {
				setLoading(false);
			}
		};
		loadProducts();
		//eslint-disable-next-line react-hooks/exhaustive-deps
	}, [product_context_copy]);

	const startShopping = () => {
		history("/");
	};

	const total_cost = product.reduce((total, item) => {
		const item_price = new BigNumber(item.itemPrice);
		const product_cost = item_price.multipliedBy(item.quantity);
		return total.plus(product_cost);
	}, new BigNumber(0.0));

	//function to deactivate showModal modal window on purchase button click
	const disableUserForm = () => setShowModal(false);

	// product data for product purchased by user to be stored in the orders collection in firestore
	const product_data = product_context.cartItems.map((eachProduct) => ({
		product_ref: doc(db, "items", eachProduct.id),
		quantity: eachProduct.quantity,
		size: eachProduct.size,
	}));

	// create object to store user's order in db
	const orders = {
		user_id: email,
		name: name,
		address: address,
		amount: parseFloat(total_cost),
		product_data: product_data,
		delivered: false,
		timeStamp: serverTimestamp(),
	};

	//api key for payment gateway
	const publicKey = "pk_test_55ea12648f90b53eddde58323bd65d2b71506b55";

	//object data to pass to paystack payment gateway
	const componentProps = {
		email,
		amount: parseFloat(total_cost * 100),
		metadata: {
			name,
			phone,
		},
		publicKey,
		text: "Checkout",
		onSuccess: (response) => {
			disableUserForm();
			setPaymentSuccess(true);
			changeBlurState(true);

			const createOrder = async (order_data) => {
				let success = false;
				let attempts = 0;
				do {
					attempts++;
					try {
						await addDoc(collection(db, "orders"), order_data);
						success = true;
					} catch (error) {
						console.error(error);
					}
				} while (!success && attempts <= 3);
			};
			const orders_copy = { ...orders };
			orders_copy.paystack_reference = response.reference;
			orders_copy.transaction_id = response.transaction;
			createOrder(orders_copy);
			product_context.emptyCart();
		},
		onClose: () => {
			disableUserForm();
			setPaymentAborted(true);
			changeBlurState(true);
		},
	};

	return (
		<>
			{loading && (
				<AppLoader color={"#A8C4B8"} loading={loading} size={20} />
			)}
			{!loading && !error && product.length !== 0 && (
				<div className={classes.card}>
					<p className={classes.shoppingCart}>
						<img
							src="./images/shopping-bag.png"
							alt="shopping-bag"
							width={20}
						/>{" "}
						Shopping Cart ({product_context.cartItems.length})
					</p>
					<div className={classes.productsCard}>
						<CartProductList product={product} />
					</div>
					<Row>
						<Col sm={12} md={6} lg={6} xl={6} xxl={6}>
							<div
								style={{
									display: "flex",
									flexDirection: "column",
									borderRadius: "4px",
									padding: "1rem",
									margin: "2rem",
									backgroundColor: "#f5f5f5",
									alignItems: "center",
								}}
							>
								<h5
									style={{
										borderBottom: "1px solid #ccc",
										padding: "0.5rem",
									}}
								>
									Thanks for shopping with us.
								</h5>
								<p
									style={{
										borderBottom: "1px solid #ccc",
										padding: "0.5rem",
										fontSize: "1.2rem",
										fontFamily: "Roboto, sans-serif",
										fontWeight: "400",
									}}
								>
									Total Products:{" "}
									{product_context.cartItems.length} <br />
								</p>
								<p
									style={{
										borderBottom: "1px solid #ccc",
										padding: "0.5rem",
										fontSize: "1.2rem",
										fontFamily: "Roboto, sans-serif",
										fontWeight: "400",
									}}
								>
									Total Amount: ${parseFloat(total_cost)}
								</p>
								<Button
									variant="success"
									onClick={startShopping}
									style={{
										width: "100%",
										borderRadius: "4px",
									}}
								>
									Continue Shopping{" "}
									<img
										src="./images/external-link.png"
										alt="continue-shopping"
										width={20}
									/>
								</Button>
							</div>
						</Col>
						<Col sm={12} md={6} lg={6} xl={6} xxl={6}>
							<div className={classes.checkOut}>
								<h5
									style={{
										borderBottom: "1px solid #ccc",
										padding: "0.5rem",
									}}
								>
									Proceed to CheckOut
								</h5>
								<div
									style={{
										display: "flex",
										flex: "1",
										alignItems: "center",
										justifyContent: "space-between",
										borderBottom: "1px solid #ccc",
										padding: "0.5rem",
										paddingBottom: "0rem",
									}}
								>
									<p
										style={{
											fontSize: "1.2rem",
											fontFamily: "Roboto, sans-serif",
											fontWeight: "400",
										}}
									>
										SubTotal (
										{product_context.cartItems.length}):
									</p>
									<p
										style={{
											fontSize: "1.5rem",
											fontFamily: "Roboto, sans-serif",
											fontWeight: "400",
										}}
									>
										${parseFloat(total_cost)}
									</p>
								</div>

								<Button
									style={{
										marginTop: "1rem",
										marginBottom: "0.5rem",
										borderRadius: "4px",
										width: "100%",
									}}
									variant="success"
									onClick={() => {
										setShowModal(true);
										changeBlurState(true);
									}}
								>
									Purchase{" "}
									<img
										src="./images/purchase.png"
										alt="purchase"
										width={20}
									/>
								</Button>
							</div>
						</Col>
					</Row>
				</div>
			)}

			{!loading && product.length === 0 && !error && (
				<div className={classes.card}>
					<div className={classes.header}>
						<img
							src="/images/shopping-cart.png"
							alt="shopping cart"
						/>
						<h6>Your Cart is empty!</h6>
						<p>Click on the button below to start your shopping</p>
						<Button
							className="mt-1 mb-3 rounded-1"
							variant="success"
							onClick={startShopping}
						>
							Start Shopping
						</Button>
					</div>
				</div>
			)}

			{!loading && error && (
				<Container>
					<div
						style={{
							display: "flex",
							alignContent: "center",
							justifyContent: "center",
							width: "100%",
							marginTop: "5%",
							marginLeft: "1rem",
							marginRight: "1rem",
						}}
					>
						<Alert>
							Sorry there was an error loading the product(s),
							please try again!!
						</Alert>
					</div>
				</Container>
			)}

			{/*Modal for displaying payment details */}
			<Modal
				show={showModal}
				onHide={() => {
					setShowModal(false);
					changeBlurState(false);
				}}
				size="lg"
				centered
			>
				<Modal.Header closeButton>
					<Modal.Title>CheckOut Product(s)</Modal.Title>
				</Modal.Header>

				<Modal.Body style={{ fontFamily: "Roboto, sans-serif" }}>
					<div id="submit">
						<Form.Group controlId="email">
							<Form.Label>Email Address</Form.Label>
							<Form.Control
								type="email"
								required
								className={classes.cardNumber}
								placeholder="email@domain.com"
								value={email}
								autoFocus
								onChange={(e) => setEmail(e.target.value)}
							/>
						</Form.Group>
						<Form.Group controlId="name">
							<Form.Label>Name</Form.Label>
							<Form.Control
								required
								type="text"
								placeholder="Legal Name"
								className={classes.cardNumber}
								value={name}
								onChange={(e) => setName(e.target.value)}
							/>
						</Form.Group>
						<Form.Group controlId="phone">
							<Form.Label>Phone Number</Form.Label>
							<Form.Control
								type="phone"
								className={classes.cardNumber}
								required
								placeholder="phone number"
								value={phone}
								onChange={(e) => setPhone(e.target.value)}
							/>
						</Form.Group>
						<Form.Group controlId="address">
							<Form.Label>Billing Address</Form.Label>
							<div>
								<Row>
									<Col
										xs={6}
										sm={6}
										md={6}
										lg={6}
										xl={6}
										xxl={6}
									>
										<Form.Control
											type="address"
											className={classes.cardNumber}
											required
											placeholder="House/Apt Number"
											value={address.houseNumber}
											onChange={(e) =>
												setAddress((currentData) => ({
													...currentData,
													houseNumber: e.target.value,
												}))
											}
										/>
									</Col>
									<Col
										xs={6}
										sm={6}
										md={6}
										lg={6}
										xl={6}
										xxl={6}
									>
										<Form.Control
											type="address"
											className={classes.cardNumber}
											required
											placeholder="Street"
											value={address.street}
											onChange={(e) =>
												setAddress((currentData) => ({
													...currentData,
													street: e.target.value,
												}))
											}
										/>
									</Col>
								</Row>
								<Row>
									<Col
										xs={6}
										sm={6}
										md={6}
										lg={6}
										xl={6}
										xxl={6}
									>
										<Form.Control
											type="address"
											className={classes.cardNumber}
											required
											placeholder="Town"
											value={address.town}
											onChange={(e) =>
												setAddress((currentData) => ({
													...currentData,
													town: e.target.value,
												}))
											}
										/>
									</Col>
									<Col
										xs={6}
										sm={6}
										md={6}
										lg={6}
										xl={6}
										xxl={6}
									>
										<Form.Control
											type="address"
											className={classes.cardNumber}
											required
											placeholder="State"
											value={address.state}
											onChange={(e) =>
												setAddress((currentData) => ({
													...currentData,
													state: e.target.value,
												}))
											}
										/>
									</Col>
								</Row>
								<Row>
									<Col
										xs={6}
										sm={6}
										md={6}
										lg={6}
										xl={6}
										xxl={6}
									>
										<Form.Control
											type="address"
											className={classes.cardNumber}
											required
											placeholder="Country"
											value={address.country}
											onChange={(e) =>
												setAddress((currentData) => ({
													...currentData,
													country: e.target.value,
												}))
											}
										/>
									</Col>
									<Col
										xs={6}
										sm={6}
										md={6}
										lg={6}
										xl={6}
										xxl={6}
									>
										<Form.Control
											type="address"
											className={classes.cardNumber}
											required
											placeholder="Postal Code"
											value={address.postalCode}
											onChange={(e) =>
												setAddress((currentData) => ({
													...currentData,
													postalCode: e.target.value,
												}))
											}
										/>
									</Col>
								</Row>
							</div>
						</Form.Group>
					</div>
				</Modal.Body>
				<Modal.Footer>
					{(!email ||
						!name ||
						!phone ||
						!address.houseNumber ||
						!address.street ||
						!address.town ||
						!address.state ||
						!address.country ||
						!address.postalCode) && (
						<div
							style={{
								width: "100%",
								textAlign: "center",
								fontFamily: "Libre BaskerVille, sans-serif",
							}}
						>
							<p>Fill out all the fields to proceed</p>
						</div>
					)}
					{email &&
						name &&
						phone &&
						address.houseNumber &&
						address.street &&
						address.town &&
						address.state &&
						address.country &&
						address.postalCode && (
							<PaystackButton
								className={classes.paystackBtn}
								{...componentProps}
							/>
						)}
				</Modal.Footer>
			</Modal>

			{/*Modal for displaying payment operation success */}
			<Modal
				show={paymentSuccess}
				onHide={() => {
					setPaymentSuccess(false);
					changeBlurState(false);
				}}
				centered
				size="sm"
			>
				<Modal.Header closeButton>
					<Modal.Title>Payment Successfull!!</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<p style={{ fontFamily: "Roboto, sans-serif" }}>
						Payment completed successfully!!
					</p>
				</Modal.Body>
				<Modal.Footer>
					<Button
						className="rounded-1"
						variant="success"
						onClick={() => {
							setPaymentSuccess(false);
							changeBlurState(false);
						}}
					>
						Done{" "}
						<img
							src="./images/successful.png"
							alt="successful"
							width={25}
						/>
					</Button>
				</Modal.Footer>
			</Modal>

			{/*Modal for displaying payment abortion*/}
			<Modal
				show={paymentAborted}
				onHide={() => {
					setPaymentAborted(false);
					changeBlurState(false);
				}}
				centered
				size="sm"
			>
				<Modal.Header closeButton>
					<Modal.Title>Payment Aborted!!</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<p style={{ fontFamily: "Roboto, sans-serif" }}>
						Payment was not successfull !!
					</p>
				</Modal.Body>
				<Modal.Footer>
					<Button
						variant="danger"
						onClick={() => {
							setPaymentAborted(false);
							changeBlurState(false);
						}}
						className="rounded-1"
					>
						Done{" "}
						<img src="./images/abort.png" alt="abort" width={20} />
					</Button>
				</Modal.Footer>
			</Modal>
		</>
	);
}
export default Cart;
